import { Dialog, DialogActions, DialogContent } from '@mui/material'
import { Form, Formik } from 'formik'
import * as Yup from 'yup'
import { useTranslation } from 'react-i18next'
import { DialogTitleClosable } from '@dhl/components'
import { Alert, Box, Button, Divider, Grid, H5 } from '@dhl-ui/components'
import { TextFieldInput } from '@dhl/formik'
import type { OrganisationAddressDto } from '@dhl/mdp-api'
import { COUNTRIES } from '~/common/config/country'
import { CountrySelect } from '~/common/components/addresses'

export type OrganisationAddressFormValues = {
  id?: string
  countryCode: string
  postalCode: string
  number: string
  addition: string
  street: string
  city: string
  confirmed: boolean
}

type Props = {
  address?: OrganisationAddressDto
  defaultCountryCode: string
  onSubmit: (values: OrganisationAddressFormValues) => Promise<void>
  onClose: () => void
}

export const OrganisationAddressForm = ({ address, defaultCountryCode, onSubmit, onClose }: Props) => {
  const { t } = useTranslation()

  const validationSchema = Yup.object()
    .shape({
      countryCode: Yup.string().required(t('admin.organisationsPage.error')),
      postalCode: Yup.string().trim().required(t('admin.organisationsPage.error')),
      number: Yup.string().trim().required(t('admin.organisationsPage.error')),
      addition: Yup.string(),
      street: Yup.string().trim().required(t('admin.organisationsPage.error')),
      city: Yup.string().trim().required(t('admin.organisationsPage.error')),
      confirmed: Yup.boolean().required(),
    })
    .defined()

  const initialValues: OrganisationAddressFormValues = {
    id: address?.id,
    countryCode: address?.countryCode ?? defaultCountryCode,
    postalCode: address?.postalCode ?? '',
    number: address?.number ?? '',
    addition: address?.addition ?? '',
    street: address?.street ?? '',
    city: address?.city ?? '',
    confirmed: address?.confirmed ?? false,
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validateOnMount
      validationSchema={validationSchema}
      enableReinitialize
    >
      {({ isSubmitting, isValid, handleSubmit, setFieldValue }) => (
        <Form data-testid="organisation-address-form">
          <Dialog open fullWidth maxWidth="sm" onClose={onClose}>
            <DialogTitleClosable onClose={onClose}>
              <H5>{t(`admin.organisationsPage.addresses.${address ? 'editTitle' : 'addTitle'}`)}</H5>
            </DialogTitleClosable>
            <Divider />
            <DialogContent>
              <Alert severity="info" sx={{ mb: 2 }}>
                {t('admin.organisationsPage.addresses.validationWarning')}
              </Alert>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <CountrySelect
                    fieldName="countryCode"
                    countries={COUNTRIES}
                    onChangeCountryCode={(countryCode) => setFieldValue('countryCode', countryCode)}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextFieldInput
                    fullWidth
                    label={t('admin.organisationsPage.organisationForm.postalCode')}
                    name="postalCode"
                    required
                  />
                </Grid>
                <Grid size={{ xs: 8 }}>
                  <TextFieldInput
                    fullWidth
                    label={t('admin.organisationsPage.organisationForm.number')}
                    name="number"
                    required
                  />
                </Grid>
                <Grid size={{ xs: 4 }}>
                  <TextFieldInput
                    fullWidth
                    label={t('admin.organisationsPage.organisationForm.addition')}
                    name="addition"
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextFieldInput
                    fullWidth
                    label={t('admin.organisationsPage.organisationForm.street')}
                    name="street"
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextFieldInput
                    fullWidth
                    label={t('admin.organisationsPage.organisationForm.city')}
                    name="city"
                    required
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <Divider />
            <DialogActions>
              <Box display="flex" justifyContent="flex-end" gap={1}>
                <Button color="primary" onClick={onClose}>
                  {t('admin.accountNumbers.cancel')}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={!isValid || isSubmitting}
                  onClick={() => handleSubmit()}
                >
                  {t('admin.organisationsPage.addresses.save')}
                </Button>
              </Box>
            </DialogActions>
          </Dialog>
        </Form>
      )}
    </Formik>
  )
}
